import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../utils/api';
import useXHR from '../hooks/useXHR';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const WS_BASE = API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '');

// Mini map component using Leaflet
const MiniMap = ({ points }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (!mapInstanceRef.current) {
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [30, 0], zoom: 2, zoomControl: true,
        attributionControl: false, scrollWheelZoom: true
      });
      L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 18
      }).addTo(mapInstanceRef.current);
    }

    // Clear old markers
    markersRef.current.forEach(m => mapInstanceRef.current.removeLayer(m));
    markersRef.current = [];

    if (points && points.length > 0) {
      const bounds = [];
      points.forEach(p => {
        if (p.lat && p.lng) {
          const icon = L.divIcon({
            className: 'collab-map-dot',
            html: `<div style="width:10px;height:10px;border-radius:50%;background:${p.color || '#FDE047'};border:2px solid #334155;box-shadow:0 0 6px ${p.color || '#FDE047'};"></div>`,
            iconSize: [14, 14], iconAnchor: [7, 7]
          });
          const marker = L.marker([p.lat, p.lng], { icon }).addTo(mapInstanceRef.current);
          marker.bindPopup(`<div style="font-size:12px"><b>${p.name || ''}</b><br/><span style="color:#888">${p.title || ''}</span></div>`);
          markersRef.current.push(marker);
          bounds.push([p.lat, p.lng]);
        }
      });
      if (bounds.length > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [20, 20], maxZoom: 8 });
      }
    }
  }, [points]);

  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  return <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 10 }} />;
};

const CollaborationPage = ({ showToast }) => {
  const { token, user } = useAuth();
  const { get, post, del } = useXHR(token);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null);
  const [roomDetail, setRoomDetail] = useState(null);
  const [newRoomName, setNewRoomName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [message, setMessage] = useState('');
  const [pinUrl, setPinUrl] = useState('');
  const [pinTitle, setPinTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [typingUsers, setTypingUsers] = useState([]);
  // Collaborative search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [lastSearch, setLastSearch] = useState(null);
  const [mapPoints, setMapPoints] = useState([]);
  const [activeTab, setActiveTab] = useState('chat'); // chat | search | map
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);

  const fetchRooms = useCallback(() => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/collab-rooms/rooms`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onreadystatechange = function() {
      if (x.readyState === 4 && x.status === 200) {
        try { setRooms(JSON.parse(x.responseText).rooms || []); } catch {}
      }
    };
    x.send();
  }, [token]);

  const fetchRoomDetail = useCallback((roomId) => {
    if (!token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/collab-rooms/rooms/${roomId}`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onreadystatechange = function() {
      if (x.readyState === 4 && x.status === 200) {
        try { setRoomDetail(JSON.parse(x.responseText)); } catch {}
      }
    };
    x.send();
  }, [token]);

  useEffect(() => {
    if (!token) return;
    fetchRooms();
  }, [token, fetchRooms]);

  // WebSocket connection management
  useEffect(() => {
    if (!activeRoom || !token) return;
    fetchRoomDetail(activeRoom);
    setSearchResults([]);
    setMapPoints([]);
    setLastSearch(null);

    const wsUrl = `${WS_BASE}/api/ws/collab/${activeRoom}?token=${token}`;
    let ws;
    let reconnectTimer;

    const connect = () => {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => setWsConnected(true);

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case 'new_message':
              setRoomDetail(prev => prev ? {
                ...prev,
                messages: [...(prev.messages || []), data.message]
              } : prev);
              break;
            case 'new_pin':
              setRoomDetail(prev => prev ? {
                ...prev,
                shared_state: {
                  ...prev.shared_state,
                  pinned_urls: [...(prev.shared_state?.pinned_urls || []), data.pin]
                }
              } : prev);
              break;
            case 'user_joined':
            case 'user_left':
              setActiveUsers(data.active_users || []);
              break;
            case 'typing':
              if (data.is_typing) {
                setTypingUsers(prev => prev.includes(data.username) ? prev : [...prev, data.username]);
                setTimeout(() => setTypingUsers(prev => prev.filter(u => u !== data.username)), 3000);
              } else {
                setTypingUsers(prev => prev.filter(u => u !== data.username));
              }
              break;
            // Collaborative search events
            case 'search_started':
              setSearchLoading(true);
              setLastSearch({ query: data.query, by: data.username });
              if (data.username !== user?.username) {
                showToast(`${data.username} started a search: "${data.query}"`, 'info');
              }
              break;
            case 'search_results':
              setSearchLoading(false);
              setSearchResults(data.results || []);
              setLastSearch({ query: data.query, by: data.username, total: data.total });
              // Build map points from results
              const pts = [];
              const colors = ['#FDE047', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#FDE68A'];
              (data.results || []).forEach((r, ri) => {
                (r.locations || []).forEach(loc => {
                  if (loc.lat && loc.lng) {
                    pts.push({
                      lat: loc.lat, lng: loc.lng, name: loc.name,
                      title: r.title, color: colors[ri % colors.length]
                    });
                  }
                });
              });
              setMapPoints(pts);
              setActiveTab('search');
              break;
            case 'search_error':
              setSearchLoading(false);
              showToast(`Search failed: ${data.error}`, 'error');
              break;
            default:
              break;
          }
        } catch {}
      };

      ws.onclose = (event) => {
        setWsConnected(false);
        if (event.code !== 1000) reconnectTimer = setTimeout(connect, 10000);
      };
      ws.onerror = () => setWsConnected(false);
    };

    connect();

    return () => {
      clearTimeout(reconnectTimer);
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
      setWsConnected(false);
      setActiveUsers([]);
      setTypingUsers([]);
    };
  }, [activeRoom, token, fetchRoomDetail, user?.username, showToast]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [roomDetail?.messages]);

  const createRoom = () => {
    if (!newRoomName.trim()) return;
    setLoading(true);
    post('/collab-rooms/rooms', {
      body: { name: newRoomName.trim(), type: 'search' },
      onSuccess: (data) => {
        showToast(`Room "${newRoomName}" created! Code: ${data.room_id}`, 'success');
        setRooms(prev => [{ room_id: data.room_id, name: newRoomName.trim(), type: 'search',
          owner_name: user?.username || 'You', is_owner: true, participant_count: 1,
          participants: [{ username: user?.username || 'You', is_owner: true }],
          created_at: new Date().toISOString(), updated_at: new Date().toISOString()
        }, ...prev]);
        setNewRoomName('');
        setActiveRoom(data.room_id);
      },
      onFinally: () => setLoading(false)
    });
  };

  const joinRoom = () => {
    if (!joinCode.trim()) return;
    post(`/collab-rooms/rooms/${joinCode.trim()}/join`, {
      onSuccess: (data) => {
        showToast(`Joined room: ${data.name || joinCode}`, 'success');
        setJoinCode('');
        fetchRooms();
        setActiveRoom(joinCode.trim());
      },
      onError: () => showToast('Room not found or already closed', 'error')
    });
  };

  const sendMessage = () => {
    if (!message.trim() || !activeRoom) return;
    const text = message.trim();
    setMessage('');
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'chat_message', message: text }));
    } else {
      post(`/collab-rooms/rooms/${activeRoom}/message`, {
        body: { message: text }, onSuccess: () => fetchRoomDetail(activeRoom)
      });
    }
  };

  const handleTyping = () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: true }));
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'typing', is_typing: false }));
        }
      }, 2000);
    }
  };

  const pinUrlToRoom = () => {
    if (!pinUrl.trim() || !activeRoom) return;
    const url = pinUrl.trim();
    const title = pinTitle.trim();
    setPinUrl(''); setPinTitle('');
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'pin_url', url, title }));
      showToast('URL pinned!', 'success');
    } else {
      post(`/collab-rooms/rooms/${activeRoom}/pin`, {
        body: { url, title },
        onSuccess: () => { showToast('URL pinned!', 'success'); fetchRoomDetail(activeRoom); }
      });
    }
  };

  // Collaborative search via WebSocket with REST API fallback
  const triggerCollabSearch = () => {
    if (!searchQuery.trim() || !activeRoom) return;
    const query = searchQuery.trim();
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'collaborative_search', query }));
      setSearchLoading(true);
    } else {
      // REST API fallback for mobile/unreliable WebSocket
      setSearchLoading(true);
      const x = new XMLHttpRequest();
      x.open('POST', `${API}/collab-rooms/rooms/${activeRoom}/search`, true);
      x.setRequestHeader('Content-Type', 'application/json');
      x.setRequestHeader('Authorization', `Bearer ${token}`);
      x.onreadystatechange = function() {
        if (x.readyState === 4) {
          setSearchLoading(false);
          if (x.status === 200) {
            try {
              const data = JSON.parse(x.responseText);
              setSearchResults(data.results || []);
              setLastSearch({ query, by: user?.username, total: data.total });
              const pts = [];
              const colors = ['#FDE047', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#FDE68A'];
              (data.results || []).forEach((r, ri) => {
                (r.locations || []).forEach(loc => {
                  if (loc.lat && loc.lng) {
                    pts.push({ lat: loc.lat, lng: loc.lng, name: loc.name, title: r.title, color: colors[ri % colors.length] });
                  }
                });
              });
              setMapPoints(pts);
              setActiveTab('search');
            } catch { showToast('Failed to parse search results', 'error'); }
          } else {
            showToast('Search failed. Please try again.', 'error');
          }
        }
      };
      x.send(JSON.stringify({ query }));
    }
  };

  const closeRoom = (roomId) => {
    del(`/collab-rooms/rooms/${roomId}`, {
      onSuccess: () => {
        showToast('Room closed', 'success');
        if (activeRoom === roomId) { setActiveRoom(null); setRoomDetail(null); }
        fetchRooms();
      }
    });
  };

  const inputStyle = { background: 'rgba(253, 251, 247,0.8)', border: '1px solid rgba(253, 224, 71,0.25)', borderRadius: 8, color: '#334155', padding: '8px 12px', fontSize: '0.8rem' };

  // Annotations state
  const [annotations, setAnnotations] = useState([]);
  const [annotationText, setAnnotationText] = useState('');
  const [annotationTarget, setAnnotationTarget] = useState('');

  const fetchAnnotations = useCallback(() => {
    if (!activeRoom || !token) return;
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/collab-rooms/rooms/${activeRoom}/annotations`, true);
    x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onreadystatechange = function() {
      if (x.readyState === 4 && x.status === 200) {
        try { setAnnotations(JSON.parse(x.responseText).annotations || []); } catch {}
      }
    };
    x.send();
  }, [activeRoom, token]);

  useEffect(() => { if (activeRoom) fetchAnnotations(); }, [activeRoom, fetchAnnotations]);

  const addAnnotation = () => {
    if (!annotationText.trim() || !activeRoom) return;
    post(`/collab-rooms/rooms/${activeRoom}/annotations`, {
      body: { text: annotationText.trim(), target_url: annotationTarget.trim() || '', lat: null, lng: null },
      onSuccess: (data) => {
        setAnnotations(prev => [...prev, data.annotation]);
        setAnnotationText('');
        setAnnotationTarget('');
        showToast('Annotation added!', 'success');
      }
    });
  };

  const deleteAnnotation = (annotationId) => {
    del(`/collab-rooms/rooms/${activeRoom}/annotations/${annotationId}`, {
      onSuccess: () => {
        setAnnotations(prev => prev.filter(a => a.annotation_id !== annotationId));
        showToast('Annotation deleted', 'success');
      }
    });
  };

  return (
    <div className="card" data-testid="collaboration-page" style={{ maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 20, color: '#c4b5fd', display: 'flex', alignItems: 'center', gap: 10 }}>
        <i className="fas fa-users" style={{ color: '#3b82f6' }} />
        Real-time Collaboration
        {wsConnected && activeRoom && (
          <span style={{ fontSize: '0.6rem', background: 'rgba(16,185,129,0.2)', color: '#10b981', padding: '2px 8px', borderRadius: 10, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            data-testid="ws-status-connected">
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />
            Live
          </span>
        )}
      </h2>

      <div style={{ display: 'grid', gridTemplateColumns: activeRoom ? '260px 1fr' : '1fr', gap: 16, minHeight: 500 }}>
        {/* Left Panel: Room List + Create/Join */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Create Room */}
          <div style={{ background: '#475569', borderRadius: 12, padding: 14, border: '1px solid rgba(245, 158, 11,0.2)' }}>
            <h4 style={{ color: '#60a5fa', fontSize: '0.82rem', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="fas fa-plus-circle" />Create Room
            </h4>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newRoomName} onChange={e => setNewRoomName(e.target.value)}
                placeholder="Room name..." data-testid="new-room-name"
                onKeyDown={e => e.key === 'Enter' && createRoom()}
                style={{ ...inputStyle, flex: 1, border: '1px solid rgba(245, 158, 11,0.25)' }} />
              <button onClick={createRoom} disabled={loading || !newRoomName.trim()} data-testid="create-room-btn"
                style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#0F172A', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, opacity: !newRoomName.trim() ? 0.5 : 1 }}>
                Create
              </button>
            </div>
          </div>

          {/* Join Room */}
          <div style={{ background: '#475569', borderRadius: 12, padding: 14, border: '1px solid rgba(16,185,129,0.2)' }}>
            <h4 style={{ color: '#10b981', fontSize: '0.82rem', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="fas fa-sign-in-alt" />Join Room
            </h4>
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={joinCode} onChange={e => setJoinCode(e.target.value)}
                placeholder="Room code..." data-testid="join-room-code"
                onKeyDown={e => e.key === 'Enter' && joinRoom()}
                style={{ ...inputStyle, flex: 1, border: '1px solid rgba(16,185,129,0.25)' }} />
              <button onClick={joinRoom} disabled={!joinCode.trim()} data-testid="join-room-btn"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#0F172A', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, opacity: !joinCode.trim() ? 0.5 : 1 }}>
                Join
              </button>
            </div>
          </div>

          {/* Room List */}
          <div style={{ background: '#475569', borderRadius: 12, padding: 14, border: '1px solid rgba(253, 224, 71,0.15)', flex: 1, overflowY: 'auto', maxHeight: 360 }}>
            <h4 style={{ color: '#047857', fontSize: '0.82rem', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="fas fa-door-open" />Your Rooms ({rooms.length})
            </h4>
            {rooms.length === 0 ? (
              <p style={{ color: '#555', fontSize: '0.75rem', textAlign: 'center', padding: 20 }}>
                No rooms yet. Create one or join with a code!
              </p>
            ) : rooms.map(room => (
              <div key={room.room_id} onClick={() => setActiveRoom(room.room_id)}
                data-testid={`room-item-${room.room_id}`}
                style={{
                  padding: '10px 12px', marginBottom: 6, borderRadius: 8, cursor: 'pointer',
                  background: activeRoom === room.room_id ? 'rgba(253, 224, 71,0.15)' : 'rgba(255, 255, 255, 0.85)',
                  border: `1px solid ${activeRoom === room.room_id ? 'rgba(253, 224, 71,0.4)' : 'rgba(255,255,255,0.06)'}`,
                  transition: 'all 0.15s'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#334155', fontSize: '0.8rem', fontWeight: 600 }}>{room.name}</span>
                  {room.is_owner && (
                    <button onClick={(e) => { e.stopPropagation(); closeRoom(room.room_id); }}
                      data-testid={`close-room-${room.room_id}`}
                      style={{ background: 'rgba(239,68,68,0.15)', border: 'none', color: '#f87171', padding: '2px 6px', borderRadius: 4, cursor: 'pointer', fontSize: '0.55rem' }}>
                      Close
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <span style={{ color: '#71717a', fontSize: '0.6rem' }}>
                    <i className="fas fa-users" style={{ marginRight: 3 }} />{room.participant_count}
                  </span>
                  <span style={{ color: '#555', fontSize: '0.6rem', fontFamily: 'monospace' }}>#{room.room_id}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panel: Active Room */}
        {activeRoom && roomDetail ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Room Header */}
            <div style={{
              background: '#475569', borderRadius: 12, padding: '12px 16px',
              border: '1px solid rgba(245, 158, 11,0.2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8
            }}>
              <div>
                <h3 style={{ margin: 0, color: '#60a5fa', fontSize: '1rem' }}>{roomDetail.name}</h3>
                <div style={{ display: 'flex', gap: 10, marginTop: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ color: '#71717a', fontSize: '0.68rem' }}>
                    Code: <strong style={{ color: '#047857', fontFamily: 'monospace' }}>{roomDetail.room_id}</strong>
                  </span>
                  <button onClick={() => { navigator.clipboard.writeText(roomDetail.room_id); showToast('Copied!', 'success'); }}
                    data-testid="copy-room-code"
                    style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', fontSize: '0.58rem', textDecoration: 'underline' }}>Copy</button>
                  {wsConnected && <span style={{ fontSize: '0.55rem', color: '#10b981' }}><i className="fas fa-bolt" style={{ marginRight: 3 }} />Live</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {roomDetail.participants?.map(p => (
                  <span key={p.user_id} style={{
                    background: activeUsers.includes(p.username) ? 'rgba(16,185,129,0.15)' : p.is_owner ? 'rgba(245,158,11,0.15)' : 'rgba(255, 255, 255, 0.85)',
                    color: activeUsers.includes(p.username) ? '#10b981' : p.is_owner ? '#f59e0b' : '#71717a',
                    padding: '2px 7px', borderRadius: 8, fontSize: '0.62rem', fontWeight: 600,
                    display: 'flex', alignItems: 'center', gap: 3
                  }}>
                    {activeUsers.includes(p.username) && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />}
                    {p.username} {p.is_owner ? '(owner)' : ''}
                  </span>
                ))}
              </div>
            </div>

            {/* Collaborative Search Bar */}
            <div style={{
              background: '#475569', borderRadius: 12, padding: '10px 14px',
              border: '1px solid rgba(253, 224, 71,0.25)', display: 'flex', gap: 8, alignItems: 'center'
            }}>
              <i className="fas fa-search" style={{ color: '#047857', fontSize: '0.85rem' }} />
              <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search together... results shared with all participants"
                data-testid="collab-search-input"
                onKeyDown={e => e.key === 'Enter' && triggerCollabSearch()}
                style={{ ...inputStyle, flex: 1, border: 'none', background: 'transparent', fontSize: '0.82rem' }} />
              <button onClick={triggerCollabSearch} disabled={searchLoading || !searchQuery.trim()}
                data-testid="collab-search-btn"
                style={{
                  background: searchLoading ? 'rgba(253, 224, 71,0.2)' : 'linear-gradient(135deg, #FDE047, #FCD34D)',
                  color: '#0F172A', border: 'none', borderRadius: 8, padding: '10px 18px', cursor: 'pointer',
                  fontSize: '0.8rem', fontWeight: 700, whiteSpace: 'nowrap',
                  opacity: !searchQuery.trim() ? 0.5 : 1,
                  minHeight: 44, minWidth: 44, touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'rgba(253, 224, 71,0.3)'
                }}>
                {searchLoading ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 4 }} />Searching...</> : 'Search'}
              </button>
            </div>

            {/* Tab Switcher */}
            <div style={{ display: 'flex', gap: 4 }}>
              {[
                { id: 'chat', label: 'Chat', icon: 'fa-comments', count: roomDetail.messages?.length },
                { id: 'search', label: 'Search Results', icon: 'fa-search', count: searchResults.length },
                { id: 'map', label: 'Map', icon: 'fa-map-marker-alt', count: mapPoints.length },
                { id: 'annotations', label: 'Annotations', icon: 'fa-sticky-note', count: annotations.length }
              ].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  data-testid={`tab-${tab.id}`}
                  style={{
                    flex: 1, padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                    background: activeTab === tab.id ? 'rgba(253, 224, 71,0.2)' : 'rgba(255, 255, 255, 0.85)',
                    border: `1px solid ${activeTab === tab.id ? 'rgba(253, 224, 71,0.4)' : 'rgba(255,255,255,0.06)'}`,
                    color: activeTab === tab.id ? '#047857' : '#71717a',
                    fontSize: '0.75rem', fontWeight: 600, transition: 'all 0.15s'
                  }}>
                  <i className={`fas ${tab.icon}`} style={{ marginRight: 5 }} />
                  {tab.label} {tab.count > 0 && <span style={{ opacity: 0.7 }}>({tab.count})</span>}
                </button>
              ))}
            </div>

            {/* Content Area */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 12, flex: 1 }}>
              {/* Main Content */}
              <div style={{ background: '#475569', borderRadius: 12, border: '1px solid rgba(253, 224, 71,0.15)', display: 'flex', flexDirection: 'column', minHeight: 350 }}>
                {activeTab === 'chat' && (
                  <>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
                      <h4 style={{ margin: 0, color: '#047857', fontSize: '0.8rem' }}>
                        <i className="fas fa-comments" style={{ marginRight: 6 }} />Room Chat
                      </h4>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px', maxHeight: 300 }} data-testid="chat-messages">
                      {roomDetail.messages?.length === 0 ? (
                        <p style={{ color: '#475569', fontSize: '0.8rem', textAlign: 'center', padding: 20 }}>No messages yet.</p>
                      ) : roomDetail.messages?.map((msg, i) => (
                        <div key={msg.id || i} style={{ marginBottom: 8, padding: '6px 10px', borderRadius: 8, background: 'rgba(14, 165, 233, 0.08)', border: '1px solid rgba(14, 165, 233, 0.18)' }} data-testid={`chat-msg-${i}`}>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                            <span style={{ color: '#0369A1', fontSize: '0.72rem', fontWeight: 700 }}>{msg.username}</span>
                            <span style={{ color: '#64748B', fontSize: '0.6rem' }}>{msg.timestamp?.slice(11, 16)}</span>
                          </div>
                          <p style={{ color: '#0F172A', fontSize: '0.82rem', margin: '3px 0 0', lineHeight: 1.45, wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.message}</p>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                    {typingUsers.length > 0 && (
                      <div style={{ padding: '2px 14px', color: '#475569', fontSize: '0.65rem', fontStyle: 'italic' }} data-testid="typing-indicator">
                        {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
                      </div>
                    )}
                    <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(16, 185, 129, 0.18)', display: 'flex', gap: 6 }}>
                      <input value={message} onChange={e => { setMessage(e.target.value); handleTyping(); }}
                        placeholder="Type a message... (Enter to send)" data-testid="room-message-input"
                        onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        style={{ ...inputStyle, flex: 1, background: '#ffffff', color: '#0F172A', border: '1px solid rgba(14, 165, 233, 0.28)' }} />
                      <button onClick={sendMessage} data-testid="send-message-btn"
                        style={{ background: 'linear-gradient(135deg, #10B981, #059669)', color: '#ffffff', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' }}>
                        Send
                      </button>
                    </div>
                  </>
                )}

                {activeTab === 'search' && (
                  <>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{ margin: 0, color: '#047857', fontSize: '0.8rem' }}>
                        <i className="fas fa-search" style={{ marginRight: 6 }} />
                        Shared Search Results
                        {lastSearch && <span style={{ fontWeight: 400, color: '#71717a', marginLeft: 8, fontSize: '0.7rem' }}>
                          "{lastSearch.query}" by {lastSearch.by} ({lastSearch.total || searchResults.length} results)
                        </span>}
                      </h4>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px', maxHeight: 340 }} data-testid="collab-search-results">
                      {searchResults.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 30, color: '#555' }}>
                          <i className="fas fa-search" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block', opacity: 0.3 }} />
                          <p style={{ fontSize: '0.8rem' }}>Use the search bar to find URLs collaboratively.</p>
                          <p style={{ fontSize: '0.7rem', color: '#444' }}>Results appear here for all room participants in real-time.</p>
                        </div>
                      ) : searchResults.map((r, i) => (
                        <div key={r.id || i} style={{
                          padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                          background: 'rgba(255, 255, 255, 0.85)', border: '1px solid rgba(255,255,255,0.06)',
                          transition: 'border-color 0.15s'
                        }} data-testid={`search-result-${i}`}>
                          <a href={r.url} target="_blank" rel="noopener noreferrer"
                            style={{ color: '#93c5fd', fontSize: '0.78rem', fontWeight: 600, textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.title || r.url}
                          </a>
                          <p style={{ color: '#a1a1aa', fontSize: '0.7rem', margin: '4px 0', lineHeight: 1.4 }}>{r.snippet}</p>
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {r.locations?.length > 0 && (
                              <span style={{ fontSize: '0.6rem', color: '#10b981', background: 'rgba(16,185,129,0.1)', padding: '1px 6px', borderRadius: 4 }}>
                                <i className="fas fa-map-marker-alt" style={{ marginRight: 3 }} />
                                {r.locations.length} location{r.locations.length > 1 ? 's' : ''}
                              </span>
                            )}
                            {r.categories?.slice(0, 3).map(c => (
                              <span key={c} style={{ fontSize: '0.58rem', color: '#047857', background: 'rgba(253, 224, 71,0.1)', padding: '1px 5px', borderRadius: 3 }}>{c}</span>
                            ))}
                            <button onClick={() => { pinUrlToRoom(); setPinUrl(r.url); setPinTitle(r.title); }}
                              style={{ background: 'none', border: 'none', color: '#f59e0b', cursor: 'pointer', fontSize: '0.58rem', marginLeft: 'auto' }}>
                              <i className="fas fa-thumbtack" style={{ marginRight: 3 }} />Pin
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {activeTab === 'map' && (
                  <>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
                      <h4 style={{ margin: 0, color: '#047857', fontSize: '0.8rem' }}>
                        <i className="fas fa-map-marker-alt" style={{ marginRight: 6 }} />
                        Search Results Map
                        <span style={{ fontWeight: 400, color: '#71717a', marginLeft: 8, fontSize: '0.7rem' }}>
                          {mapPoints.length} location{mapPoints.length !== 1 ? 's' : ''} mapped
                        </span>
                      </h4>
                    </div>
                    <div style={{ flex: 1, minHeight: 300 }} data-testid="collab-map">
                      {mapPoints.length > 0 ? (
                        <MiniMap points={mapPoints} />
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#555' }}>
                          <div style={{ textAlign: 'center' }}>
                            <i className="fas fa-globe" style={{ fontSize: '2rem', marginBottom: 10, opacity: 0.3, display: 'block' }} />
                            <p style={{ fontSize: '0.8rem' }}>Search to see locations on the map.</p>
                            <p style={{ fontSize: '0.7rem', color: '#444' }}>Each dot represents an address found within search results.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {activeTab === 'annotations' && (
                  <>
                    <div style={{ padding: '8px 14px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
                      <h4 style={{ margin: 0, color: '#f59e0b', fontSize: '0.8rem' }}>
                        <i className="fas fa-sticky-note" style={{ marginRight: 6 }} />
                        Annotations ({annotations.length})
                      </h4>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 14px', maxHeight: 300 }} data-testid="annotations-list">
                      {annotations.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: 30, color: '#555' }}>
                          <i className="fas fa-sticky-note" style={{ fontSize: '1.5rem', marginBottom: 10, display: 'block', opacity: 0.3 }} />
                          <p style={{ fontSize: '0.8rem' }}>No annotations yet.</p>
                          <p style={{ fontSize: '0.7rem', color: '#444' }}>Add comments to specific URLs or map points.</p>
                        </div>
                      ) : annotations.map((a, i) => (
                        <div key={a.annotation_id} data-testid={`annotation-${i}`} style={{
                          padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                          background: 'rgba(245,158,11,0.05)', border: '1px solid rgba(245,158,11,0.15)'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline', marginBottom: 4 }}>
                                <span style={{ color: '#f59e0b', fontSize: '0.72rem', fontWeight: 700 }}>{a.username}</span>
                                <span style={{ color: '#444', fontSize: '0.52rem' }}>{a.created_at?.slice(0, 16)}</span>
                              </div>
                              <p style={{ color: '#d4d4d8', fontSize: '0.78rem', margin: 0, lineHeight: 1.5 }}>{a.text}</p>
                              {a.target_url && (
                                <a href={a.target_url} target="_blank" rel="noopener noreferrer"
                                  style={{ color: '#60a5fa', fontSize: '0.65rem', textDecoration: 'none', marginTop: 4, display: 'inline-block' }}>
                                  <i className="fas fa-link" style={{ marginRight: 4 }} />{a.target_url.length > 50 ? a.target_url.slice(0, 50) + '...' : a.target_url}
                                </a>
                              )}
                            </div>
                            {(a.user_id === String(user?._id || user?.id) || roomDetail?.is_owner) && (
                              <button onClick={() => deleteAnnotation(a.annotation_id)}
                                data-testid={`delete-annotation-${i}`}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.6rem', padding: 4, opacity: 0.6 }}>
                                <i className="fas fa-trash" />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div style={{ padding: '8px 14px', borderTop: '1px solid rgba(255, 255, 255, 0.85)' }}>
                      <input value={annotationTarget} onChange={e => setAnnotationTarget(e.target.value)}
                        placeholder="Target URL (optional)" data-testid="annotation-target-input"
                        style={{ ...inputStyle, width: '100%', marginBottom: 4, fontSize: '0.7rem', padding: '5px 8px', boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 6 }}>
                        <input value={annotationText} onChange={e => setAnnotationText(e.target.value)}
                          placeholder="Add annotation..." data-testid="annotation-text-input"
                          onKeyDown={e => e.key === 'Enter' && addAnnotation()}
                          style={{ ...inputStyle, flex: 1 }} />
                        <button onClick={addAnnotation} disabled={!annotationText.trim()} data-testid="add-annotation-btn"
                          style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#0F172A', border: 'none', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, opacity: !annotationText.trim() ? 0.5 : 1 }}>
                          Add
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Right Sidebar: Pinned URLs */}
              <div style={{
                background: '#475569', borderRadius: 12, border: '1px solid rgba(245,158,11,0.15)',
                display: 'flex', flexDirection: 'column'
              }}>
                <div style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}>
                  <h4 style={{ margin: 0, color: '#f59e0b', fontSize: '0.78rem' }}>
                    <i className="fas fa-thumbtack" style={{ marginRight: 6 }} />Pinned ({roomDetail.shared_state?.pinned_urls?.length || 0})
                  </h4>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '6px 12px', maxHeight: 260 }} data-testid="pinned-urls-list">
                  {(roomDetail.shared_state?.pinned_urls || []).length === 0 ? (
                    <p style={{ color: '#444', fontSize: '0.7rem', textAlign: 'center', padding: 16 }}>No pinned URLs.</p>
                  ) : (roomDetail.shared_state?.pinned_urls || []).map((pin, i) => (
                    <div key={i} style={{ padding: '5px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.85)' }}
                      data-testid={`pinned-url-${i}`}>
                      <a href={pin.url} target="_blank" rel="noopener noreferrer"
                        style={{ color: '#93c5fd', fontSize: '0.68rem', textDecoration: 'none', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {pin.title || pin.url}
                      </a>
                      <span style={{ color: '#555', fontSize: '0.52rem' }}>by {pin.pinned_by}</span>
                    </div>
                  ))}
                </div>
                <div style={{ padding: '6px 12px', borderTop: '1px solid rgba(255, 255, 255, 0.85)' }}>
                  <input value={pinTitle} onChange={e => setPinTitle(e.target.value)}
                    placeholder="Title (optional)" data-testid="pin-title-input"
                    style={{ ...inputStyle, width: '100%', marginBottom: 4, fontSize: '0.7rem', padding: '5px 8px', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 4 }}>
                    <input value={pinUrl} onChange={e => setPinUrl(e.target.value)}
                      placeholder="URL to pin..." data-testid="pin-url-input"
                      onKeyDown={e => e.key === 'Enter' && pinUrlToRoom()}
                      style={{ ...inputStyle, flex: 1, fontSize: '0.7rem', padding: '5px 8px' }} />
                    <button onClick={pinUrlToRoom} data-testid="pin-url-btn"
                      style={{ background: 'rgba(245,158,11,0.2)', border: '1px solid rgba(245,158,11,0.4)', color: '#f59e0b', borderRadius: 6, padding: '5px 8px', cursor: 'pointer', fontSize: '0.62rem', fontWeight: 700 }}>
                      Pin
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : !activeRoom && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1,
            background: 'rgba(255, 255, 255,0.3)', borderRadius: 14, border: '1px dashed rgba(203, 213, 225, 0.8)'
          }}>
            <div style={{ textAlign: 'center', color: '#555' }}>
              <i className="fas fa-users" style={{ fontSize: '2.5rem', marginBottom: 16, opacity: 0.3, display: 'block' }} />
              <p style={{ fontSize: '0.9rem', marginBottom: 6 }}>Select or create a room to start collaborating</p>
              <p style={{ fontSize: '0.7rem', color: '#444' }}>Search together, share URLs, chat in real-time, and visualize findings on a shared map</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollaborationPage;
